import { parse } from "date-fns";

const MILISEGUNDOS_POR_DIA = 24 * 60 * 60 * 1000;
const DIAS_SIN_PAGAR_VERDE = 2;
const DIAS_SIN_PAGAR_AMARILLO = 7;
const DIAS_SIN_PAGAR_ROJO = 8;

type FechaCliente =
  | Date
  | number
  | string
  | {
      toDate?: () => Date;
      toMillis?: () => number;
    }
  | null
  | undefined;

type HistorialPagoCliente = {
  fecha?: FechaCliente;
};

type ClienteConEstadoPago = {
  fechaFinal?: FechaCliente;
  fechaUltimoAbono?: FechaCliente;
  formaDePago?: string | null;
  historialPagos?: HistorialPagoCliente[] | null;
};

const normalizarInicioDelDia = (fecha: Date): Date => {
  const fechaNormalizada = new Date(fecha);
  fechaNormalizada.setHours(0, 0, 0, 0);
  return fechaNormalizada;
};

export const normalizarFechaCliente = (fecha: FechaCliente): Date | null => {
  if (!fecha) {
    return null;
  }

  if (fecha instanceof Date) {
    return Number.isNaN(fecha.getTime()) ? null : new Date(fecha.getTime());
  }

  if (typeof fecha === "number") {
    const fechaNormalizada = new Date(fecha);
    return Number.isNaN(fechaNormalizada.getTime()) ? null : fechaNormalizada;
  }

  if (typeof fecha === "object") {
    if (typeof fecha.toDate === "function") {
      return normalizarFechaCliente(fecha.toDate());
    }

    if (typeof fecha.toMillis === "function") {
      return normalizarFechaCliente(fecha.toMillis());
    }
  }

  if (typeof fecha !== "string") {
    return null;
  }

  const fechaLimpia = fecha.trim();

  if (!fechaLimpia) {
    return null;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(fechaLimpia)) {
    const fechaParseada = parse(fechaLimpia, "dd/MM/yyyy", new Date());

    return Number.isNaN(fechaParseada.getTime()) ? null : fechaParseada;
  }

  const fechaNormalizada = new Date(fechaLimpia);

  return Number.isNaN(fechaNormalizada.getTime()) ? null : fechaNormalizada;
};

export const obtenerUltimaFechaPagoCliente = (cliente: ClienteConEstadoPago): Date | null => {
  const fechasPago = [normalizarFechaCliente(cliente.fechaUltimoAbono)];

  if (Array.isArray(cliente.historialPagos)) {
    cliente.historialPagos.forEach((pago) => {
      fechasPago.push(normalizarFechaCliente(pago?.fecha));
    });
  }

  return fechasPago.reduce<Date | null>((ultimaFecha, fechaPagoActual) => {
    if (!fechaPagoActual) {
      return ultimaFecha;
    }

    if (!ultimaFecha || fechaPagoActual.getTime() > ultimaFecha.getTime()) {
      return fechaPagoActual;
    }

    return ultimaFecha;
  }, null);
};

export const calcularDiasSinPagarCliente = (
  cliente: ClienteConEstadoPago,
  fechaReferencia: Date = new Date(),
): number => {
  const ultimaFechaPago = obtenerUltimaFechaPagoCliente(cliente);

  if (!ultimaFechaPago) {
    return 0;
  }

  const fechaReferenciaNormalizada = normalizarInicioDelDia(fechaReferencia);
  const ultimaFechaPagoNormalizada = normalizarInicioDelDia(ultimaFechaPago);
  const diferenciaEnMilisegundos =
    fechaReferenciaNormalizada.getTime() - ultimaFechaPagoNormalizada.getTime();

  return Math.max(0, Math.floor(diferenciaEnMilisegundos / MILISEGUNDOS_POR_DIA));
};

export const clienteEstaVencido = (
  cliente: ClienteConEstadoPago,
  fechaReferencia: Date = new Date(),
): boolean => {
  const fechaFinal = normalizarFechaCliente(cliente.fechaFinal);

  if (!fechaFinal) {
    return false;
  }

  return (
    normalizarInicioDelDia(fechaFinal).getTime() < normalizarInicioDelDia(fechaReferencia).getTime()
  );
};

export const obtenerClaseEstadoPagoCliente = (
  cliente: ClienteConEstadoPago,
  fechaReferencia: Date = new Date(),
): string => {
  const formaDePago = String(cliente.formaDePago ?? "")
    .trim()
    .toLowerCase();

  if (formaDePago === "semanal") {
    return "bg-orange-950 text-white";
  }

  if (formaDePago === "mensual") {
    return "bg-blue-700 text-white";
  }

  const diasSinPagar = calcularDiasSinPagarCliente(cliente, fechaReferencia);

  if (clienteEstaVencido(cliente, fechaReferencia) || diasSinPagar >= DIAS_SIN_PAGAR_ROJO) {
    return "bg-red-500";
  }

  if (diasSinPagar >= DIAS_SIN_PAGAR_AMARILLO) {
    return "bg-yellow-500";
  }

  if (diasSinPagar >= DIAS_SIN_PAGAR_VERDE) {
    return "bg-green-500";
  }

  return "";
};
